import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, Button, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useStyles } from '../styles';
import { DropdownIcon } from 'assets/svgComponents/DropdownIcon';

export enum ProfileTabIndex {
  PROFILE_INFORMATION = 0,
  LOGIN_INFORMATION = 1,
  CHANGE_PASSWORD = 2,
}

interface ProfileTabsProps {
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  hideProfileInformation?: boolean;
  hasNoPassword?: boolean;
}

export const ProfileTabs: React.FC<ProfileTabsProps> = ({
  activeTab,
  onTabChange,
  hideProfileInformation,
  hasNoPassword,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabAnchorEl, setTabAnchorEl] = useState<null | HTMLElement>(null);

  // A worker who has no password yet is locked to the Create Password tab — nothing else in
  // their profile is reachable until they set one (CH-4515).
  const tabOptions = hasNoPassword
    ? [{ value: ProfileTabIndex.CHANGE_PASSWORD, key: 'create-password', label: t('myProfile.tabs.createPassword') }]
    : [
        ...(hideProfileInformation
          ? []
          : [
              {
                value: ProfileTabIndex.PROFILE_INFORMATION,
                key: 'profile-information',
                label: t('myProfile.tabs.profileInformation'),
              },
            ]),
        {
          value: ProfileTabIndex.LOGIN_INFORMATION,
          key: 'login-information',
          label: t('myProfile.tabs.loginInformation'),
        },
        { value: ProfileTabIndex.CHANGE_PASSWORD, key: 'change-password', label: t('myProfile.tabs.changePassword') },
      ];

  const handleTabMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setTabAnchorEl(event.currentTarget);
  };

  const handleTabMenuClose = () => {
    setTabAnchorEl(null);
  };

  const handleTabMenuItemClick = (value: number) => {
    onTabChange({} as React.SyntheticEvent, value);
    handleTabMenuClose();
  };

  return (
    <Box className={classes.tabsContainer} data-testid="my-profile-tabs-container">
      {isMobile ? (
        <>
          <Button
            onClick={handleTabMenuClick}
            className={classes.tabDropdown}
            data-testid="my-profile-tab-dropdown-button"
            endIcon={<DropdownIcon />}
          >
            {tabOptions.find(option => option.value === activeTab)?.label}
          </Button>
          <Menu
            anchorEl={tabAnchorEl}
            open={Boolean(tabAnchorEl)}
            onClose={handleTabMenuClose}
            className={classes.dropdownMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {tabOptions.map(option => (
              <MenuItem
                key={option.value}
                data-testid={`my-profile-tab-dropdown-item-${option.key}`}
                onClick={() => handleTabMenuItemClick(option.value)}
                className={classes.dropdownMenuItem}
                selected={activeTab === option.value}
              >
                <Typography component="span">{option.label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <Tabs value={activeTab} onChange={onTabChange} className={classes.tabs} data-testid="my-profile-tabs">
          {tabOptions.map(option => (
            <Tab
              key={option.value}
              value={option.value}
              label={option.label}
              className={classes.tab}
              data-testid={`my-profile-tab-${option.key}`}
            />
          ))}
        </Tabs>
      )}
    </Box>
  );
};
